// components/ProjectManagement.jsx
import { useContext, useEffect, useState } from "react"
import { AppContext } from "../App"
import { getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";
import HorizontalImagesStrip from "../ones/HorizontalImagesStrip";

import banner1 from '../assets/banner1.jpg';
import banner2 from '../assets/banner2.jpg';
import banner3 from '../assets/banner3.jpg';
import banner4 from '../assets/banner4.jpg';

import {
  IoMdCheckmarkCircleOutline,
  IoMdTimer,
  IoMdGitNetwork,
  IoMdBuild,
  IoMdArrowDropright,
  IoMdTrendingUp,
  IoMdPeople,
  IoMdDocument,
  IoMdGlobe,
  IoMdCash
} from "react-icons/io";
import { FaBullseye, FaHandHoldingUsd, FaFileContract, FaChartPie } from "react-icons/fa";

export default function ProjectManagement(props) {

  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  
  // Banner rotation for hero section
  const banners = [banner1, banner2, banner3, banner4, banner1, banner3];

  // Project Management Service Pillars - ATRIC Profile Aligned
  const servicePillars = [
    {
      id: 1,
      title: "Project Planning & Oversight",
      icon: <FaBullseye />,
      description: "End-to-end project lifecycle management from scoping and scheduling to execution oversight, ensuring alignment with strategic objectives and stakeholder expectations.",
      features: [
        "Detailed Project Scoping, WBS & Resource Allocation",
        "Gantt Charts, Critical Path Analysis & Milestone Tracking",
        "Stakeholder Engagement Plans & Communication Frameworks"
      ],
      gradient: "from-blue-500/20 to-indigo-500/10",
      accent: "#60a5fa"
    },
    {
      id: 2,
      title: "Fund Management & Tracking",
      icon: <FaHandHoldingUsd />,
      description: "Professional oversight of project finances including expenditure tracking, fund mobilization, donor compliance, and financial reporting to ensure transparency and accountability.",
      features: [
        "Project Budget Development & Cash Flow Forecasting",
        "Expenditure Tracking, Variance Analysis & Financial Reporting",
        "Donor Compliance, Grant Management & Audit Readiness"
      ],
      gradient: "from-emerald-500/20 to-teal-500/10",
      accent: "#10b981"
    },
    {
      id: 3,
      title: "Investment Facilitation (TIC/EPZ)",
      icon: <FaFileContract />,
      description: "Specialized support for local and foreign investors seeking Tanzania Investment Centre certificates, Strategic Investment Status, and Special Economic Zone registration.",
      features: [
        "TIC Certificate of Incentives Application & Renewal Support",
        "Strategic & Special Strategic Investment Status Applications",
        "EPZ Registration, EIA Coordination & License Renewals"
      ],
      gradient: "from-violet-500/20 to-purple-500/10",
      accent: "#a78bfa"
    },
    {
      id: 4,
      title: "M&E and Compliance Reporting",
      icon: <FaChartPie />,
      description: "Robust monitoring & evaluation frameworks with timely reporting to TIC, EPZA, and other regulatory bodies to ensure compliance and demonstrate project impact.",
      features: [
        "KPI Development, Baseline Studies & Impact Assessments",
        "Quarterly & Annual Returns Preparation for TIC/EPZA",
        "Government Liaison, Policy Advocacy & Regulatory Representation"
      ],
      gradient: "from-amber-500/20 to-orange-500/10",
      accent: "#f59e0b"
    }
  ];

  // Project Lifecycle Methodology Steps
  const methodologySteps = [
    {
      step: "01",
      title: "Initiate & Scope",
      desc: "Define project charter, objectives, success criteria, and stakeholder map to establish clear foundation",
      icon: <IoMdDocument size={24} />,
      deliverables: ["Project Charter", "Stakeholder Register", "Initial Risk Log"]
    },
    {
      step: "02",
      title: "Plan & Structure",
      desc: "Develop detailed work breakdown structure, schedule, budget, resource plan, and risk mitigation strategies",
      icon: <IoMdGitNetwork size={24} />,
      deliverables: ["WBS & Gantt Chart", "Budget & Cash Flow Plan", "Risk Management Plan"]
    },
    {
      step: "03",
      title: "Execute & Monitor",
      desc: "Coordinate team activities, track progress against baselines, manage changes, and ensure quality delivery",
      icon: <IoMdBuild size={24} />,
      deliverables: ["Progress Reports", "Variance Analysis", "Change Control Logs"]
    },
    {
      step: "04",
      title: "Close & Evaluate",
      desc: "Formal project closure, lessons learned documentation, final reporting, and handover to operations",
      icon: <IoMdCheckmarkCircleOutline size={24} />,
      deliverables: ["Final Report", "Lessons Learned Register", "Handover Documentation"]
    }
  ];

  // Industries & Project Types We Serve
  const projectTypes = [
    { name: "Infrastructure Development", icon: "🏗️" },
    { name: "Donor-Funded Programs", icon: "🤝" },
    { name: "Private Sector Investments", icon: "💼" },
    { name: "Public Sector Initiatives", icon: "🏛️" },
    { name: "NGO & Community Projects", icon: "🌍" },
    { name: "Technology Implementation", icon: "💻" },
    { name: "Capacity Building Programs", icon: "📚" },
    { name: "FDI Facilitation Projects", icon: "🌐" }
  ];

  async function init() {
    setReady(false);
    setTimeout(() => setReady(true), 300);
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (!ready) return;
    const interval = setInterval(() => {
      setBgIndex(prev => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [ready]);

  if (!ready) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      {/* ================= HERO SECTION ================= */}
      <section className="MainBodySection position-relative">
        <div style={{
          width: "100%",
          position: "relative",
          overflow: "hidden"
        }}>
          {/* Rotating Banner Backgrounds */}
          {banners.map((banner, i) => (
            <div
              key={i}
              style={{
                backgroundImage: `url(${banner})`,
                backgroundAttachment: "fixed",
                backgroundSize: "cover",
                backgroundPosition: "center",
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                transition: "opacity 2s ease-in-out",
                opacity: i === bgIndex ? 1 : 0,
                zIndex: 0
              }}
              aria-hidden={i !== bgIndex}
            />
          ))}

          {/* Dark Overlay with Content */}
          <div style={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.85) 50%, rgba(15,23,42,0.7) 100%)",
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            backdropFilter: "blur(2px)"
          }}>
            <div className="container py-5">
              <div className="row align-items-center">
                <div className="col-lg-8">
                  <span className="d-inline-block px-3 py-1 mb-3 rounded-pill" 
                    style={{ 
                      background: "rgba(139,92,246,0.15)", 
                      border: "1px solid rgba(139,92,246,0.3)", 
                      color: "#a78bfa", 
                      fontSize: "13px", 
                      fontWeight: "500" 
                    }}>
                    Project Management Excellence
                  </span>
                  
                  <h1 className="text-light mb-3" style={{
                    fontSize: "clamp(2rem, 4vw, 3.2rem)",
                    fontWeight: "700",
                    lineHeight: "1.15",
                    letterSpacing: "-0.5px"
                  }}>
                    Delivering Projects On Time, On Budget, On Target
                  </h1>

                  <p className="text-light mt-3 mb-4" style={{
                    fontSize: "1.1rem",
                    opacity: 0.9,
                    lineHeight: "1.7",
                    maxWidth: "650px"
                  }}>
                    ATRIC provides professional project oversight, fund management, and investment facilitation services to help organizations execute strategic initiatives with confidence, compliance, and measurable impact across Tanzania and Africa.
                  </p>

                  <div className="d-flex flex-wrap gap-3">
                    <button
                      className="btn btn-primary btn-lg px-4 py-2 rounded-pill d-flex align-items-center gap-2"
                      onClick={() => appContext.navTo({ item: 'contact_us', service: 'Project Management' })}
                      style={{ 
                        background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
                        border: "none",
                        fontWeight: "500",
                        boxShadow: "0 4px 14px rgba(124, 58, 237, 0.35)"
                      }}
                    >
                      Start Your Project
                      <IoMdArrowDropright size={20} />
                    </button>
                    <a 
                      href="#methodology" 
                      className="btn btn-outline-light btn-lg px-4 py-2 rounded-pill"
                      style={{ 
                        borderColor: "rgba(255,255,255,0.3)",
                        fontWeight: "500"
                      }}
                    >
                      View Methodology
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= VALUE PROPOSITION ================= */}
      <section style={{
        background: "linear-gradient(to bottom, #0f172a 0%, #1e293b 100%)",
        padding: "70px 0"
      }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-9">
              <h2 className="text-light mb-3" style={{ fontWeight: "600", fontSize: "2rem" }}>
                Why Choose ATRIC for Project Management?
              </h2>
              <p className="text-light opacity-75 mb-4" style={{ lineHeight: "1.7", fontSize: "1.05rem" }}>
                With experienced professionals skilled in finance, auditing, and business advisory, ATRIC brings a unique integrated approach to project management—combining financial discipline, regulatory expertise, and strategic oversight to ensure your initiatives deliver sustainable value.
              </p>
              
              {/* Key Differentiators Badges */}
              <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
                {[
                  { name: "Financial Discipline", icon: <IoMdCash size={14} /> },
                  { name: "Regulatory Expertise", icon: <IoMdDocument size={14} /> },
                  { name: "Stakeholder Focus", icon: <IoMdPeople size={14} /> },
                  { name: "Results-Driven", icon: <IoMdTrendingUp size={14} /> }
                ].map((item, idx) => (
                  <div key={idx} className="badge rounded-pill px-3 py-2 d-flex align-items-center gap-2" 
                    style={{ 
                      background: "rgba(255,255,255,0.08)", 
                      border: "1px solid rgba(255,255,255,0.15)",
                      color: "#e2e8f0",
                      fontSize: "0.85rem",
                      fontWeight: "500"
                    }}>
                    {item.icon}
                    <span>{item.name}</span>
                  </div>
                ))}
              </div>

              <HorizontalImagesStrip
                images={[banner1, banner2, banner3, banner4]}
                className="mt-4"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICE PILLARS GRID ================= */}
      <section style={{
        background: "#0f172a",
        padding: "80px 0"
      }} id="services">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="text-light mb-3" style={{ fontWeight: "600", fontSize: "2.2rem" }}>
              Integrated Project Management Services
            </h2>
            <p className="text-light opacity-75" style={{ maxWidth: "700px", margin: "0 auto", lineHeight: "1.6" }}>
              Four specialized pillars delivering end-to-end project success—from strategic planning and fund oversight to regulatory compliance and impact measurement.
            </p>
          </div>

          <div className="row g-4">
            {servicePillars.map((pillar) => (
              <div className="col-md-6 col-lg-3" key={pillar.id}>
                <div 
                  className="h-100 p-4 rounded-3 border position-relative overflow-hidden"
                  style={{
                    background: `linear-gradient(145deg, rgba(30,41,59,0.8), rgba(15,23,42,0.9))`,
                    backdropFilter: "blur(12px)",
                    borderColor: "rgba(255,255,255,0.08)",
                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow = "0 20px 40px -15px rgba(0, 0, 0, 0.4)";
                    e.currentTarget.style.borderColor = pillar.accent + "66";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Learn more about ${pillar.title}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      appContext.navTo({ item: 'contact_us', service: pillar.title });
                    }
                  }}
                >
                  {/* Decorative gradient accent */}
                  <div className="position-absolute top-0 end-0 p-3 opacity-20" 
                    style={{ 
                      background: `linear-gradient(135deg, ${pillar.gradient.split(' ')[0].replace('/20','/40')}, ${pillar.gradient.split(' ')[1].replace('/10','/25')})`,
                      width: "80px",
                      height: "80px",
                      borderRadius: "0 0 0 100%",
                      zIndex: 0
                    }} 
                  />

                  {/* Icon Badge */}
                  <div className="d-inline-flex align-items-center justify-content-center rounded-2 p-2 mb-3"
                    style={{
                      background: `linear-gradient(135deg, ${pillar.gradient})`,
                      border: `1px solid rgba(255,255,255,0.15)`,
                      color: "#fff",
                      width: "48px",
                      height: "48px",
                      fontSize: "1.3rem",
                      zIndex: 1
                    }}>
                    {pillar.icon}
                  </div>

                  {/* Service Content */}
                  <h5 className="text-light mb-2" style={{ fontWeight: "600", fontSize: "1.25rem", zIndex: 1 }}>
                    {pillar.title}
                  </h5>
                  <p className="text-light opacity-80 mb-3 small" style={{ lineHeight: "1.6", zIndex: 1 }}>
                    {pillar.description}
                  </p>

                  {/* Feature List */}
                  <ul className="list-unstyled mb-4" style={{ zIndex: 1 }}>
                    {pillar.features.map((feature, idx) => (
                      <li key={idx} className="d-flex align-items-start gap-2 mb-2 small">
                        <IoMdCheckmarkCircleOutline 
                          className="flex-shrink-0 mt-0.5" 
                          size={16} 
                          style={{ color: pillar.accent }} 
                        />
                        <span className="text-light opacity-85" style={{ fontSize: "0.9rem" }}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Link */}
                  <a 
                    href="#contact"
                    className="d-inline-flex align-items-center gap-1 text-decoration-none"
                    style={{ 
                      color: pillar.accent, 
                      fontWeight: "500", 
                      fontSize: "0.9rem",
                      zIndex: 1,
                      transition: "gap 0.2s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.gap = "6px"}
                    onMouseLeave={(e) => e.currentTarget.style.gap = "4px"}
                    onClick={(e) => {
                      e.preventDefault();
                      appContext.navTo({ item: 'contact_us', service: pillar.title });
                    }}
                  >
                    Learn More
                    <IoMdArrowDropright size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PROJECT TYPES ================= */}
      <section style={{
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        padding: "60px 0",
        borderTop: "1px solid rgba(255,255,255,0.06)"
      }}>
        <div className="container">
          <div className="text-center mb-4">
            <h3 className="text-light mb-2" style={{ fontWeight: "600" }}>
              Industries & Project Types We Support
            </h3>
          </div>
          <div className="row g-3 justify-content-center">
            {projectTypes.map((type, idx) => (
              <div className="col-6 col-sm-4 col-md-3" key={idx}>
                <div 
                  className="p-3 rounded-2 text-center"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div className="mb-2" style={{ fontSize: "1.5rem" }}>{type.icon}</div>
                  <span className="text-light small" style={{ fontWeight: "500", fontSize: "0.85rem" }}>
                    {type.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= METHODOLOGY ================= */}
      <section id="methodology" style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        padding: "75px 0"
      }}>
        <div className="container">
          <div className="text-center mb-5">
            <h3 className="text-light mb-2" style={{ fontWeight: "600" }}>
              Our Project Management Methodology
            </h3>
            <p className="text-light opacity-75">A structured, phased approach ensuring clarity, control, and successful delivery</p>
          </div>

          <div className="row g-4 justify-content-center">
            {methodologySteps.map((step, idx) => (
              <div className="col-md-6 col-lg-3" key={idx}>
                <div className="text-center p-4 h-100" style={{ position: "relative" }}>
                  {/* Connector line for desktop */}
                  {idx < 3 && (
                    <div className="d-none d-lg-block position-absolute top-50 end-0" 
                      style={{ 
                        width: "100%", 
                        height: "1px", 
                        background: "linear-gradient(to right, rgba(139,92,246,0.3), transparent)",
                        transform: "translateY(-50%) translateX(50%)",
                        zIndex: 0
                      }} 
                    />
                  )}
                  
                  <div className="position-relative z-1">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 mx-auto"
                      style={{
                        width: "56px",
                        height: "56px",
                        background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
                        color: "white",
                        fontWeight: "700",
                        fontSize: "1.1rem",
                        boxShadow: "0 8px 20px -5px rgba(124, 58, 237, 0.4)"
                      }}>
                      {step.icon}
                    </div>
                    <div className="badge bg-primary bg-opacity-10 text-primary mb-2" style={{ fontSize: "0.8rem", fontWeight: "600" }}>
                      Phase {step.step}
                    </div>
                    <h6 className="text-light mb-2" style={{ fontWeight: "600" }}>{step.title}</h6>
                    <p className="text-light opacity-75 small mb-2" style={{ lineHeight: "1.6" }}>{step.desc}</p>
                    
                    {/* Key Deliverables */}
                    <div className="mt-3 pt-2 border-top" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                      <small className="text-light opacity-60 d-block mb-1">Key Deliverables:</small>
                      {step.deliverables.map((del, i) => (
                        <span key={i} className="badge me-1 mb-1" style={{ 
                          background: "rgba(255,255,255,0.1)", 
                          fontSize: "0.75rem",
                          fontWeight: "400"
                        }}>
                          {del}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INVESTMENT FACILITATION HIGHLIGHT ================= */}
      <section style={{
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        padding: "60px 0",
        borderTop: "1px solid rgba(255,255,255,0.06)"
      }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="p-4 rounded-3" style={{
                background: "rgba(139,92,246,0.1)",
                border: "1px solid rgba(139,92,246,0.3)"
              }}>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <IoMdGlobe size={32} style={{ color: "#a78bfa" }} />
                  <h4 className="text-light mb-0" style={{ fontWeight: "600" }}>Investment Facilitation Expertise</h4>
                </div>
                <p className="text-light opacity-85 mb-3" style={{ lineHeight: "1.6" }}>
                  ATRIC specializes in guiding investors through Tanzania's regulatory landscape. We provide end-to-end support for:
                </p>
                <ul className="list-unstyled mb-0">
                  {[
                    "TIC Certificates of Incentives (New & Renewal)",
                    "Strategic Investment Status Applications",
                    "EPZ Registration & Environmental Compliance",
                    "Import Duty Exemption Processing with TRA",
                    "Liaison with Government Authorities & Policy Makers"
                  ].map((item, idx) => (
                    <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                      <IoMdCheckmarkCircleOutline size={18} style={{ color: "#10b981", flexShrink: 0 }} />
                      <span className="text-light opacity-90 small">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="text-center text-lg-start">
                <h3 className="text-light mb-3" style={{ fontWeight: "600" }}>
                  Accelerate Your Investment in Tanzania
                </h3>
                <p className="text-light opacity-80 mb-4" style={{ lineHeight: "1.7" }}>
                  Whether you're a local entrepreneur or foreign investor, our team simplifies complex regulatory processes so you can focus on growing your business. We combine technical expertise with on-the-ground relationships to secure approvals efficiently.
                </p>
                <a 
                  href="#contact"
                  className="d-inline-flex align-items-center gap-2 text-decoration-none"
                  style={{ color: "#a78bfa", fontWeight: "500" }}
                  onClick={(e) => {
                    e.preventDefault();
                    appContext.navTo({ item: 'contact_us', service: 'Investment Facilitation' });
                  }}
                >
                  Discuss Your Investment Goals
                  <IoMdArrowDropright size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CTA SECTION ================= */}
      <section id="contact" style={{
        background: "linear-gradient(135deg, #0c1424 0%, #1e293b 100%)",
        padding: "65px 0",
        borderTop: "1px solid rgba(255,255,255,0.08)"
      }}>
        <div className="container text-center">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <IoMdTimer size={42} className="text-primary mb-3" style={{ color: "#a78bfa" }} />
              <h3 className="text-light mb-3" style={{ fontWeight: "600" }}>
                Ready to Execute Your Next Strategic Initiative?
              </h3>
              <p className="text-light opacity-80 mb-4" style={{ lineHeight: "1.7" }}>
                Partner with ATRIC's experienced project management team for disciplined oversight, regulatory navigation, and measurable results. Let's turn your vision into delivered value.
              </p>
              
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <button
                  className="btn btn-primary btn-lg px-5 py-2 rounded-pill"
                  onClick={() => appContext.navTo({ item: 'contact_us', service: 'Project Management' })}
                  style={{ 
                    background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
                    border: "none",
                    fontWeight: "600",
                    boxShadow: "0 6px 20px rgba(124, 58, 237, 0.3)"
                  }}
                >
                  Schedule Project Consultation
                </button>
                <a 
                  href="tel:+255717918808"
                  className="btn btn-outline-light btn-lg px-4 py-2 rounded-pill d-flex align-items-center gap-2"
                  style={{ borderColor: "rgba(255,255,255,0.25)" }}
                >
                  <span style={{ fontSize: "1.1rem" }}>📞</span>
                  +255 717 918 808
                </a>
              </div>
              
              <p className="text-light opacity-60 small mt-4 mb-0">
                📍 2nd Floor, The Tax Hub Building, Industrial Road, Mikocheni B, Dar es Salaam
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}